import React from "react";
import { View, Pressable, StyleSheet } from "react-native";
import { t } from "@constants/localization";
import { LOCK, STATUS, getBoxesWithName, getCardActions } from "../helpers";
import { C, RText, Tag, Check, AddressLine, tpName } from "../theme";
import { STATUS_TAG, eddText } from "./RoutingTpRow";

const PATH = "DsRouting";

/**
 * Slim TP card for the By DB screen: a checkbox when `ofd_ready`, the status
 * pill, and a Remove (un-assign) for cards that aren't locked. A PICKED BY DB
 * card is removable after a hand-off confirm (unless it has no route); one already OFD only shows its pill.
 */
const DbTpRow = ({ item, today, selected, onToggle, onRemove }) => {
  const tag = STATUS_TAG[item.status] || STATUS_TAG[STATUS.PACKED];
  const actions = getCardActions(item);
  const id = `${item.cx_id}-${item.date}`;
  const place = [
    item.area,
    item.racks?.length
      ? t(`${PATH}/rack`, { racks: item.racks.join(", ") })
      : null,
    eddText(item.date, today),
  ]
    .filter(Boolean)
    .join(" · ");
  const pickedByDb = actions.lock === LOCK.PICKED_BY_DB;
  const removable =
    item.status !== STATUS.OFD && Boolean(item.db) && actions.remove;
  const boxesWith = getBoxesWithName(item);

  return (
    <View style={styles.card} testID={`ds-routing-db-tp-${id}`}>
      <View style={styles.head}>
        {item.ofd_ready ? (
          <Pressable
            testID={`ds-routing-db-check-${id}`}
            onPress={() => onToggle(item)}
            hitSlop={8}
            accessibilityRole="checkbox"
            accessibilityState={{ checked: Boolean(selected) }}
            accessibilityLabel={tpName(item)}
            style={[styles.box, selected && styles.boxOn]}
          >
            {selected ? <Check size={14} /> : null}
          </Pressable>
        ) : null}
        <View style={styles.grow}>
          <RText size={16} weight={700} style={styles.tight}>
            {tpName(item)}
          </RText>
          <RText size={12} weight={600} color={C.muted}>
            {t(`${PATH}/${item.order_count === 1 ? "order" : "orders"}`, {
              count: String(item.order_count ?? 0),
            })}
          </RText>
        </View>
        <Tag
          label={t(`${PATH}/status/${tag.key}`)}
          bg={tag.bg}
          color={tag.color}
        />
      </View>
      <AddressLine address={item.address} style={styles.address} />
      {place ? (
        <RText size={12} weight={500} color={C.muted} style={styles.place}>
          {place}
        </RText>
      ) : null}
      {boxesWith ? (
        <RText size={12} weight={600} color={C.amber} style={styles.boxesWith}>
          {t(`${PATH}/boxesWith`, { name: boxesWith, db: item.db?.name || "" })}
        </RText>
      ) : null}
      {removable || pickedByDb ? (
        <View style={styles.footer}>
          {pickedByDb ? (
            <RText size={12} weight={600} color={C.blue} style={styles.grow}>
              {t(`${PATH}/pickedByNote`, { name: item.db?.name || "" })}
            </RText>
          ) : (
            <View style={styles.grow} />
          )}
          {removable ? (
            <Pressable
              testID={`ds-routing-db-remove-${id}`}
              onPress={() => onRemove(item)}
              hitSlop={8}
              style={styles.remove}
            >
              <RText size={12} weight={700} color={C.chipRed}>
                {t(`${PATH}/byDb/remove`)}
              </RText>
            </Pressable>
          ) : null}
        </View>
      ) : null}
    </View>
  );
};

export default DbTpRow;

const styles = StyleSheet.create({
  card: {
    backgroundColor: C.surface,
    borderWidth: 1,
    borderColor: C.border,
    borderRadius: 12,
    padding: 15,
    marginHorizontal: 16,
    marginBottom: 12,
  },
  head: { flexDirection: "row", alignItems: "center", gap: 10 },
  grow: { flex: 1 },
  tight: { letterSpacing: -0.2 },
  address: { paddingTop: 4 },
  place: { paddingTop: 3 },
  boxesWith: {
    backgroundColor: C.amberBg,
    borderRadius: 8,
    paddingVertical: 6,
    paddingHorizontal: 10,
    marginTop: 8,
  },
  box: {
    width: 22,
    height: 22,
    borderRadius: 6,
    borderWidth: 1.5,
    borderColor: C.border,
    backgroundColor: C.surface,
    alignItems: "center",
    justifyContent: "center",
  },
  boxOn: { backgroundColor: C.green, borderColor: C.green },
  footer: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
    marginTop: 10,
    paddingTop: 10,
    borderTopWidth: 1,
    borderTopColor: C.border,
  },
  remove: {
    borderWidth: 1.5,
    borderColor: C.chipRed,
    borderRadius: 8,
    paddingVertical: 4,
    paddingHorizontal: 12,
  },
});
